import { defineModel } from "../define-model";

export type ScheduleKind =
	| "flow_run"
	| "analytics_sync"
	| "competitor_scrape"
	| "content_plan_refresh"
	/** Mirrors an agent's schedule trigger (`targetId` = agent); edit it on the agent. */
	| "agent_run";

export type AutoSchedule = {
	enabled: boolean;
	socialAccountIds: string[];
	mode: "draft" | "approval" | "auto";
};

export type Schedule = {
	id: string;
	workspaceId: string;
	projectId: string;
	name: string;
	kind: ScheduleKind;
	targetId: string | null;
	cron: string;
	timezone: string;
	payload: {
		inputs?: Record<string, unknown>;
		autoSchedule?: AutoSchedule;
		/** Mirrors this `trigger.schedule` node of the target flow (server-managed). */
		nodeId?: string;
		/** Why a trigger-backed schedule can't run (plan, invalid cron…). */
		problem?: string | null;
		/** `agent_run` rows: the agent and its trigger key (server-managed). */
		agentId?: string;
		triggerKey?: string;
		prompt?: string | null;
	};
	enabled: boolean;
	nextRunAt: string | null;
	lastRunAt: string | null;
	lastRunStatus: "queued" | "running" | "succeeded" | "failed" | "cancelled" | "skipped" | null;
	lastError: string | null;
	createdById: string | null;
	createdAt: string;
	updatedAt: string;
};

export const triggerNodeOf = (schedule: Pick<Schedule, "payload">) =>
	schedule.payload?.nodeId ?? null;

declare module "../types" {
	interface SyncModels {
		schedule: Schedule;
	}
}

export const model = defineModel({
	name: "schedule",
	load: "eager",
	indexes: ["projectId"],
	defaults: ({ userId, workspaceId, now }) => ({
		workspaceId,
		targetId: null,
		timezone: "UTC",
		payload: {},
		enabled: true,
		nextRunAt: null,
		lastRunAt: null,
		lastRunStatus: null,
		lastError: null,
		createdById: userId,
		createdAt: now,
		updatedAt: now,
	}),
	sort: (a, b) => a.createdAt.localeCompare(b.createdAt),
});
